'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

/**
 * Program search for editorial guide pages (/expanded-access, /right-to-try).
 *
 * Unlike the landing-page `Search` block (full-bleed, display-scale `rh-lg`
 * title), this renders at article width in the guides' visual language: the
 * patient beige fill and a Selawik heading at content scale, so it reads as a
 * step in the guide rather than a landing section dropped mid-article.
 */

export interface MarketingGuideSearchProps {
  title: string;
  text?: string;
  placeholder?: string;
  buttonText?: string;
  searchUrl?: string;
}

export default function MarketingGuideSearch({
  title,
  text,
  placeholder = 'enter disease, condition, or keyword',
  buttonText = 'Search programs',
  searchUrl = '/patient/programs',
}: MarketingGuideSearchProps) {
  const [query, setQuery] = useState('');
  const router = useRouter();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    router.push(`${searchUrl}?query=${encodeURIComponent(query)}`);
  }

  return (
    <section aria-label="Program search" className="rounded-2xl bg-secondary p-5 sm:p-8">
      <p className="text-sm font-semibold uppercase tracking-wide text-default-700">
        Free nonprofit search
      </p>
      <h3 className="!pb-0 pt-1">{title}</h3>
      {text && <p className="pt-2">{text}</p>}
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-5 sm:flex-row">
        <label htmlFor="guide-search-input" className="sr-only">
          Search by disease, condition, or keyword
        </label>
        <input
          id="guide-search-input"
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="min-h-11 w-full flex-1 rounded-[50px] border-2 border-transparent bg-white px-6 py-3 text-base transition-colors duration-200 focus:outline-none focus-visible:border-primary sm:text-lg"
        />
        <button type="submit" className="rh-btn-black shrink-0">
          {buttonText}
        </button>
      </form>
    </section>
  );
}
